import React from "react";
import {
  FlightResultContent,
  selectChosenFlightsResults,
} from "../searchFlightsSlice";
import { useAppSelector } from "../../../app/hooks";
import SearchFlightsResult from "./SearchFlightsResult";
import SearchFlightsResultsSort from "./SearchFlightsResultsSort";
import { useTranslation } from "react-i18next";
import SearchFlightsResultModal from "./SearchFlightsResultModal";
import { ConfigSliceState, selectConfig } from "../../config/ConfigSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons/faExclamationTriangle";
import SearchFlightsResultITT from "./SearchFlightsResultITT";
import SearchFlightsResultANA from "./SearchFlightsResultANA";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import AllFlightsCheckbox from "./AllFlightsCheckbox";
import SearchFlightsResultsLegend from "./SearchFlightsResultsLegend";
import { RMFlightWrapper } from "../../../types";

function mergeRows(rows: RMFlightWrapper[]): RMFlightWrapper[] {
  const rowMap = new Map<string, RMFlightWrapper>();

  rows.forEach((row) => {
    const key = `${row.arrivalTimeAsString}-${row.departureTimeAsString}`;
    if (rowMap.has(key)) {
      const existingRow = rowMap.get(key)!;

      // Determine the target row based on the length of the frequency string
      const targetRow =
        existingRow.frequency.replace(/\s+/g, "").length >=
        row.frequency.replace(/\s+/g, "").length
          ? existingRow
          : row;
      const sourceRow = targetRow === existingRow ? row : existingRow;

      const mergedAvailableDatesDeparture = [
        ...new Set([
          ...targetRow.availableDatesDeparture,
          ...sourceRow.availableDatesDeparture,
        ]),
      ];

      const mergedFrequency = new Set([
        ...targetRow.frequency.replace(/\s+/g, "").split(""),
        ...sourceRow.frequency.replace(/\s+/g, "").split(""),
      ]);

      const updatedRow = {
        ...targetRow,
        availableDatesDeparture: mergedAvailableDatesDeparture,
        frequency: Array.from(mergedFrequency).sort().join(""),
        effectiveFrom: Math.min(
          targetRow.effectiveFrom,
          sourceRow.effectiveFrom
        ),
        effectiveTo: Math.max(targetRow.effectiveTo, sourceRow.effectiveTo),
      };

      // Update the map with the updated row
      rowMap.set(key, updatedRow);
    } else {
      rowMap.set(key, { ...row });
    }
  });

  return Array.from(rowMap.values());
}

function SearchFlightsResults({ layout }) {
  const { t } = useTranslation();
  const { mapper, components, app }: ConfigSliceState =
    useAppSelector(selectConfig);
  const results: FlightResultContent = useAppSelector(
    selectChosenFlightsResults
  );
  const type = useAppSelector((state) => state.searchFlights.results.type);
  const originLocation = useAppSelector(
    (state) => state.searchFlights.form.originAirport
  );
  const destinationLocation = useAppSelector(
    (state) => state.searchFlights.form.destinationAirport
  );
  const customerId = app.customerId;
  const anaClientIds = ["ANA-dom", "ANA-dom-ja", "ANA-intl", "ANA-intl-ja"];
  const isANAClient = anaClientIds.some((id) => customerId.includes(id));

  const mergeFlightResults = useAppSelector(
    (state) => state.config.components.Results.mergeFlightResults
  );

  const rows = results.filtered;
  const processedRows = mergeFlightResults ? mergeRows(rows) : rows;
  if (results && results.error) {
    return (
      <div className="notification is-danger m-1">
        <FontAwesomeIcon icon={faExclamationCircle} />
        &nbsp;
        {t("ERROR_CANNOT_RETRIEVE_RESULTS")}
      </div>
    );
  }

  if (results && !results.error && results.flights) {
    if (results.flights.length === 0) {
      if (type.startsWith("direct")) {
        return (
          <div className="notification columns is-warning is-vcentered m-1">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div className="column">
              <p className="has-text-weight-bold">
                {t("LABEL_NO_FLIGHTS_FOUND")}
              </p>
              <p>{t("HEADER_NO_DIRECT_FLIGHTS")}</p>
            </div>
          </div>
        );
      } else {
        return (
          <div className="notification columns is-warning is-vcentered m-1">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div className="column">
              <p className="has-text-weight-bold">
                {t("LABEL_NO_FLIGHTS_FOUND")}
              </p>
              <p>{t("HEADER_NO_CONNECTING_FLIGHTS")}</p>
            </div>
          </div>
        );
      }
    } else {
      return (
        <>
          <SearchFlightsResultsSort processedRows={processedRows} />
          <SearchFlightsResultModal />
          <SearchFlightsResultsLegend />
          {components.Results.format === "ITT" && (
            <div
              id="search-flights-results"
              className="flight-results"
              role="table"
            >
              <div
                className="flight-result flight-results-header"
                role="rowgroup"
              >
                <div className="columns is-mobile is-vcentered" role="row">
                  {components.Results.copyToClipboard && (
                    <div className="column is-narrow" role="columnheader">
                      <AllFlightsCheckbox />
                    </div>
                  )}
                  <div className="column">
                    <div className="columns is-mobile is-vcentered">
                      <div
                        className="column is-narrow is-hidden-mobile"
                        role="columnheader"
                      >
                        <div className="flight-result-narrow" />
                      </div>
                      <div
                        className="column is-hidden-mobile is-thirty"
                        role="columnheader"
                      >
                        {t("HEADER_SORT_AIRLINE")}
                      </div>
                      <div className="column" role="columnheader">
                        {t("FLIGHT")}
                      </div>
                      <div className="column" role="columnheader">
                        {t("INPUT_LABEL_FROM")}
                      </div>
                      <div className="column" role="columnheader">
                        {t("INPUT_LABEL_TO")}
                      </div>
                      <div className="column" role="columnheader">
                        {t("DEPARTS")}
                      </div>
                      <div className="column" role="columnheader">
                        {t("ARRIVES")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {results.filtered.map((result, i) => (
                <SearchFlightsResultITT
                  key={i}
                  dateMin={results.dateMin}
                  dateMax={results.dateMax}
                  result={result}
                  mapper={mapper}
                  components={components}
                  layout={layout}
                />
              ))}
            </div>
          )}
          {components.Results.format === "flight-finder" && (
            <div id="search-flights-results" className="flight-results">
              <table className="table is-hoverable is-bordered is-fullwidth">
                <tbody>
                  {processedRows.map((result, i) =>
                    isANAClient ? (
                      <SearchFlightsResultANA
                        key={i}
                        dateMin={results.dateMin}
                        dateMax={results.dateMax}
                        result={result}
                        mapper={mapper}
                        originLocation={originLocation}
                        destinationLocation={destinationLocation}
                      />
                    ) : (
                      <SearchFlightsResult
                        key={i}
                        dateMin={results.dateMin}
                        dateMax={results.dateMax}
                        result={result}
                        mapper={mapper}
                        originLocation={originLocation}
                        destinationLocation={destinationLocation}
                      />
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      );
    }
  } else {
    return null;
  }
}

export default SearchFlightsResults;
